import {
  FetchUserAttributesOutput,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
} from 'aws-amplify/auth';

import { MfaType, mfaRequirement } from '../types/mfa';
import { validateMfaToken } from '../lib/api';
import { CognitoUser, country } from '../types/user';
import { mfaTokenPrefix } from '../constants';

export const routerSingleParam = (param?: string | string[]) =>
  typeof param === 'string'
    ? param
    : Array.isArray(param)
      ? param[0]
      : undefined;

export function urlSafeDecode(hex: string) {
  if (hex && hex.match(/.{2}/g))
    return (hex.match(/.{2}/g) as Array<string>)
      .map(function (char: string) {
        return String.fromCharCode(parseInt(char, 16));
      })
      .join('');
  return hex || '';
}

export const getUserPoolClientData = (router: any) => {
  const WebClients =
    (process.env.userPoolWebClientIds as unknown as Record<
      string,
      Record<string, string>
    >) || {};

  const getTenancyNProduct = (clientId: string) => [
    WebClients[clientId] ? WebClients[clientId].tenancy : '',
    WebClients[clientId] && WebClients[clientId].product
      ? WebClients[clientId].product
      : '',
  ];
  if (typeof window !== 'undefined') {
    if (router && router.asPath && router.asPath.includes('client_id')) {
      if (router && router.query && router.query.client_id) {
        const clientId = router?.query?.client_id as string;
        const clientRedirect =
          (router?.query?.redirect_uri as string) ||
          (router?.query?.redirect_url as string) ||
          (WebClients[clientId] ? WebClients[clientId].redirectUrls[0] : '');
        const clientState = router?.query?.state as string;
        if (!WebClients[clientId].redirectUrls.includes(clientRedirect))
          return [];
        localStorage.setItem('clientId', clientId);
        localStorage.setItem('clientRedirect', clientRedirect);
        localStorage.setItem('clientState', clientState);
        return [
          clientId,
          clientRedirect,
          ...getTenancyNProduct(clientId),
          clientState,
        ];
      } else {
        return [];
      }
    } else if (
      router.asPath &&
      router.asPath.includes('#') &&
      (router.asPath.includes('id_token') ||
        router.asPath.includes('error_description'))
    ) {
      const clientId = localStorage.getItem('clientId') || '';
      const clientRedirect = localStorage.getItem('clientRedirect') || '';
      if (!WebClients[clientId]?.redirectUrls?.includes(clientRedirect))
        return [];
      return clientId
        ? [
            clientId,
            clientRedirect,
            ...getTenancyNProduct(clientId as string),
            localStorage.getItem('clientState'),
          ]
        : [];
    }
  }
  const defaultClientId = process.env.defaultClientId;
  const defaultRedirect = WebClients[defaultClientId as string]
    ? WebClients[defaultClientId as string].redirectUrls[0]
    : '';
  if (defaultClientId) {
    localStorage.setItem('clientId', defaultClientId);
    localStorage.setItem('clientRedirect', defaultRedirect);
    return [
      defaultClientId,
      defaultRedirect,
      ...getTenancyNProduct(defaultClientId),
    ];
  }
  return [];
};

export const getCurrentCognitoUser = async () => {
  const user = await getCurrentUser();
  const attributes = await fetchUserAttributes();
  const session = await fetchAuthSession({ forceRefresh: true });

  const cognitoUser: CognitoUser = {
    Session: null,
    attributes: {
      sub: attributes.sub || '',
      email: attributes.email || '',
      locale: (attributes.locale as country) || ('' as country),
      'custom:MFAType':
        (attributes['custom:MFAType'] as MfaType) || ('' as MfaType),
      'custom:mfaRequirement':
        (attributes['custom:mfaRequirement'] as mfaRequirement) ||
        ('' as mfaRequirement),
      // Include any other attributes, filtering out undefined values
      ...Object.entries(attributes).reduce(
        (acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, string>
      ),
    },
    keyPrefix: `CognitoIdentityServiceProvider.${session.tokens?.accessToken?.payload?.client_id}`,
    signInUserSession: {
      idToken: {
        jwtToken: session.tokens?.idToken?.toString() || '',
      },
      accessToken: {
        jwtToken: session.tokens?.accessToken?.toString() || '',
      },
    },
    storage: {},
    username: user.username,
    challengeParam: {},
  };

  return cognitoUser;
};

export const requireMFASetup = (attributes: FetchUserAttributesOutput) => {
  if (attributes['custom:mfaRequirement'] && !attributes['custom:MFAType']) {
    return true;
  }
  return false;
};

export const requireMFAValidationOld = async (
  user: CognitoUser,
  mfaToken: Record<'key' | 'value', string | undefined>
) => {
  const {
    idToken: { jwtToken: idToken },
  } = user.signInUserSession;
  const clientIdRegex = new RegExp(`${mfaTokenPrefix}.(.*).token`);
  if (user.attributes['custom:MFAType']) {
    const mfaData = {
      enabled: true,
      type: user.attributes['custom:MFAType'],
      ...(mfaToken.value && clientIdRegex.test(mfaToken.key || '')
        ? {
            token: mfaToken.value,
            clientId: (mfaToken.key || '').match(clientIdRegex)![1],
          }
        : {}),
    };

    if (mfaData.token) {
      const resp = await validateMfaToken(
        mfaData.token,
        idToken,
        mfaData.clientId!
      );
      if (resp && resp.result === 'success') {
        return false; // MFA validation dont in past 30 days
      } else {
        return true; // MFA validation has expired or invalid token
      }
    } else {
      return true; // MFA validation missing
    }
  }
  return false; // No MFA validation required
};

export const requireMFAValidation = async (
  mfaToken: string | undefined,
  clientId: string
) => {
  // Get the current auth session to access tokens
  const session = await fetchAuthSession();
  const attributes = await fetchUserAttributes();

  // Extract the ID token from the session
  const idToken = session.tokens?.idToken?.toString();

  if (!idToken) {
    throw new Error('No ID token available');
  }

  if (attributes['custom:MFAType']) {
    const mfaData = {
      enabled: true,
      type: attributes['custom:MFAType'],
      ...(mfaToken
        ? {
            token: mfaToken,
          }
        : {}),
    };

    if (mfaData.token) {
      const resp = await validateMfaToken(mfaData.token, idToken, clientId);

      if (resp && resp.result === 'success') {
        return false; // MFA validation done in past 30 days
      }
      return true; // MFA validation has expired or invalid token
    }
    return true; // MFA validation missing
  }
  return false; // No MFA validation required
};
